import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';

import { ErreurApi } from '@/shared/api/erreurs';
import { useSession } from '@/shared/auth/sessionStore';
import { useTheme } from '@/shared/theme/themeStore';

/**
 * Construit le client de requetes.
 *
 * REESSAI : on ne rejoue QUE ce qui a une chance d'aboutir. Rejouer un 403 ou un
 * 409 revient a demander trois fois de suite une chose que le serveur a refusee
 * pour une raison metier — cela retarde l'affichage de l'erreur sans rien changer.
 */
/**
 * Le porte-messages est charge paresseusement : aucune notification ne peut
 * apparaitre avant la premiere interaction, il n'a donc rien a faire sur le
 * chemin critique du premier rendu (§7).
 */
const Toaster = lazy(async () => ({ default: (await import('sonner')).Toaster }));

function creerClientRequetes(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry(nombreEchecs, erreur) {
          if (erreur instanceof ErreurApi && !erreur.estTransitoire) {
            return false;
          }

          return nombreEchecs < 2;
        },
      },
      mutations: {
        // Une mutation ne se rejoue jamais toute seule : elle peut avoir abouti
        // cote serveur malgre une reponse perdue, et la rejouer creerait un doublon.
        retry: false,
      },
    },
  });
}

/**
 * Isolation du cache entre entreprises (§0.4, §4).
 *
 * Les cles de requete sont deja prefixees par l'entreprise (voir `useCleEntreprise`),
 * ce qui suffit a ne jamais SERVIR les donnees d'une autre entreprise. On evacue
 * malgre tout celles de l'entreprise quittee : elles n'ont aucune raison de rester
 * en memoire, et une fuite ne peut pas survenir sur ce qui n'existe plus. Deux
 * garanties valent mieux qu'une pour cette regle-la.
 *
 * ON NE VIDE PAS TOUT LE CACHE, et c'est une correction, pas une facilite.
 * `client.clear()` s'executait dans un effet, donc APRES le rendu qui avait deja
 * declenche la premiere requete de la nouvelle entreprise : cette requete etait
 * supprimee en vol, sa reponse jetee, et l'ecran restait bloque sur son squelette
 * jusqu'a un remontage. Le test `shared/tenant/isolationCache.test.tsx` reproduit
 * exactement ce cas.
 *
 * Retirer seulement les entrees d'une AUTRE entreprise donne la meme garantie
 * d'etancheite sans jamais toucher la requete en cours. Cela epargne au passage
 * les entrees liees a l'UTILISATEUR (`['utilisateur', …]`, dont la liste des
 * entreprises), que `clear()` supprimait a tort — vidant le selecteur au moment
 * precis ou l'on s'en sert.
 */
function useViderCacheAuChangementDEntreprise(client: QueryClient): void {
  const entrepriseActive = useSession((etat) => etat.entrepriseActive);
  const precedente = useRef<string | null>(entrepriseActive);

  useEffect(() => {
    if (precedente.current === entrepriseActive) {
      return;
    }

    precedente.current = entrepriseActive;

    client.removeQueries({
      predicate: (requete) => {
        const [portee, identifiant] = requete.queryKey as unknown[];
        return portee === 'entreprise' && identifiant !== entrepriseActive;
      },
    });
  }, [client, entrepriseActive]);
}

function IsolationCache({ client, children }: { client: QueryClient; children: ReactNode }) {
  useViderCacheAuChangementDEntreprise(client);
  return <>{children}</>;
}

/** Restaure la session au demarrage, une seule fois. */
function RestaurationSession({ children }: { children: ReactNode }) {
  const restaurer = useSession((etat) => etat.restaurer);

  useEffect(() => {
    void restaurer();
  }, [restaurer]);

  return <>{children}</>;
}

export function Providers({ children }: { children: ReactNode }) {
  // `useState` avec initialisateur : le client est cree une fois, jamais a chaque rendu.
  const [client] = useState(creerClientRequetes);
  const theme = useTheme((etat) => etat.theme);

  return (
    <QueryClientProvider client={client}>
      <RestaurationSession>
        <IsolationCache client={client}>
          {children}

          <Suspense fallback={null}>
            <Toaster
              position="bottom-right"
              theme={theme === 'clair' ? 'light' : 'dark'}
              richColors
              closeButton
              toastOptions={{
                classNames: {
                  toast:
                    'bg-fond-eleve border-bordure text-texte shadow-astro rounded-[--radius-astro]',
                  description: 'text-texte-doux',
                },
              }}
            />
          </Suspense>
        </IsolationCache>
      </RestaurationSession>
    </QueryClientProvider>
  );
}
